import { ErrorDeLaApi } from '../api/nucleo'
import { aplanar, type Campo } from './esquema'

/**
 * Dos personas editando lo mismo (CLAUDE.md §18). El editor manda con cada guardado la
 * marca `updatedAt` de la versión desde la que editaba; si mientras tanto alguien guardó
 * otra, el núcleo responde **409** (`apps/nucleo/src/panel/concurrencia.ts`) y el panel
 * ofrece ver qué cambió, guardar encima a sabiendas o recargar.
 */
export const CABECERA_EDITADO_DESDE = 'x-mc-editado-desde'

export type Conflicto = { modificadoEn: string; modificadoPor: null | string }

/** Si un error de guardado es «alguien guardó mientras tanto», con cuándo y quién. */
export const conflictoDe = (e: unknown): Conflicto | null => {
  if (!(e instanceof ErrorDeLaApi) || e.status !== 409 || e.datos.conflicto !== true) return null
  return {
    modificadoEn: typeof e.datos.modificadoEn === 'string' ? e.datos.modificadoEn : '',
    modificadoPor: typeof e.datos.modificadoPor === 'string' && e.datos.modificadoPor ? e.datos.modificadoPor : null,
  }
}

type Valor = Record<string, unknown>

export type Diferencia = { campo: string; etiqueta: string; antes: string; ahora: string }

/** Lo que cambia de una versión a otra sin ser contenido: no se enseña. */
const IGNORAR = new Set(['id', 'createdAt', 'updatedAt', '_status', 'modificadoPor', 'esPortada', 'cliente', 'sitio'])

/** Las marcas que el editor pone a las filas nuevas, y los ids de fila, no cuentan como cambios. */
const normalizar = (valor: unknown): unknown => {
  if (Array.isArray(valor)) return valor.map(normalizar)
  if (valor && typeof valor === 'object') {
    const salida: Valor = {}
    for (const [k, v] of Object.entries(valor as Valor).sort(([a], [b]) => a.localeCompare(b))) if (k !== '_clave' && k !== 'id') salida[k] = normalizar(v)
    return salida
  }
  return valor === undefined || valor === '' ? null : valor
}

const iguales = (a: unknown, b: unknown): boolean => JSON.stringify(normalizar(a)) === JSON.stringify(normalizar(b))

/** Un valor en una línea, para leerlo, no para editarlo. */
export const resumenDeValor = (campo: Campo | undefined, valor: unknown): string => {
  if (valor === null || valor === undefined || valor === '') return '(vacío)'
  if (typeof valor === 'boolean') return valor ? 'Sí' : 'No'
  if (typeof valor === 'number') return valor.toLocaleString('es')
  if (typeof valor === 'string') {
    const opcion = campo?.opciones?.find((o) => o.valor === valor)
    const texto = opcion?.etiqueta ?? valor
    return texto.length > 80 ? `${texto.slice(0, 80)}…` : texto
  }
  if (Array.isArray(valor)) return valor.length === 0 ? '(vacío)' : `${valor.length} ${valor.length === 1 ? 'elemento' : 'elementos'}`
  return 'con cambios'
}

/**
 * Qué campos cambió la otra persona: lo que había cuando empezaste a editar frente a lo
 * que hay ahora guardado. Campo a campo de primer nivel, con el rótulo del esquema.
 */
export const diferencias = (antes: Valor, ahora: Valor, campos: Campo[]): Diferencia[] => {
  const salida: Diferencia[] = []
  for (const campo of aplanar(campos)) {
    const nombre = campo.nombre
    if (!nombre || IGNORAR.has(nombre)) continue
    if (iguales(antes[nombre], ahora[nombre])) continue
    salida.push({ campo: nombre, etiqueta: campo.etiqueta || nombre, antes: resumenDeValor(campo, antes[nombre]), ahora: resumenDeValor(campo, ahora[nombre]) })
  }
  return salida
}
